type ExportRole = "You" | "AI Analyst";

export type ExportableAssistantMessage = {
    role: ExportRole;
    text: string;
    charts?: unknown[];
};

type StoredAssistantMessage = {
    role?: unknown;
    text?: unknown;
    custom?: unknown;
};

type StoredAssistantConversation = {
    messages?: unknown;
};

const ASSISTANT_CONVERSATION_STORAGE_KEY_PREFIX = "insights-chat-";

const getStorage = (): Storage | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const normalizeRole = (role: unknown): ExportRole | null => {
    if (role === "user") return "You";
    if (role === "ai" || role === "assistant") return "AI Analyst";

    return null;
};

const normalizeText = (text: unknown): string | null => {
    if (typeof text !== "string") return null;

    const trimmed = text.trim();
    return trimmed ? trimmed : null;
};

const extractCharts = (custom: unknown): unknown[] | undefined => {
    if (Array.isArray(custom) && custom.length > 0) {
        return custom;
    }

    if (!isRecord(custom)) {
        return undefined;
    }

    const charts = custom.charts;
    if (Array.isArray(charts) && charts.length > 0) {
        return charts;
    }

    const chart = custom.chart;
    if (chart) {
        return [chart];
    }

    return undefined;
};

const normalizeStoredMessage = (
    message: unknown
): ExportableAssistantMessage | null => {
    if (!isRecord(message)) {
        return null;
    }

    const storedMessage = message as StoredAssistantMessage;
    const role = normalizeRole(storedMessage.role);
    const text = normalizeText(storedMessage.text);

    if (!role || !text) {
        return null;
    }

    const charts = extractCharts(storedMessage.custom);

    return {
        role,
        text,
        ...(charts ? { charts } : {}),
    };
};

const buildStorageKey = (channelId: string): string =>
    `${ASSISTANT_CONVERSATION_STORAGE_KEY_PREFIX}${channelId}`;

export const getExportableConversation = (
    channelId: string
): ExportableAssistantMessage[] => {
    const normalizedChannelId = channelId.trim();
    if (!normalizedChannelId) return [];

    const storage = getStorage();
    if (!storage) return [];

    try {
        const rawConversation = storage.getItem(
            buildStorageKey(normalizedChannelId)
        );
        if (!rawConversation) return [];

        const parsed = JSON.parse(
            rawConversation
        ) as StoredAssistantConversation;
        const messages = Array.isArray(parsed?.messages) ? parsed.messages : [];
        const normalizedMessages = messages
            .map((message) => normalizeStoredMessage(message))
            .filter((message): message is ExportableAssistantMessage =>
                Boolean(message)
            );

        const firstUserMessageIndex = normalizedMessages.findIndex(
            (message) => message.role === "You"
        );
        if (firstUserMessageIndex === -1) {
            return [];
        }

        return normalizedMessages.slice(firstUserMessageIndex);
    } catch {
        return [];
    }
};
